import { useEffect } from "react";
import { object, bool } from "prop-types";

import log from "../../utils/logger/library";
import {
    checkAnalyticsLoaded,
    getTrackingCounter,
    locationChanged,
} from "../../utils/analytics/library";

import {
    DEFAULT_DTM_DATA,
    DTM_WINDOW_PATH,
    MAX_LIMIT_THRESHOLD,
    INTERVAL_LENGTH,
    TRACKING_LIMIT_REACHED,
} from "./constants";

import { createUseFeatureFlagBySection } from "../../hooks/useFeatureFlag";

/**
 * This function calls a window function to clean the existing data layer
 *
 * @returns {void}
 */
function cleanDataLayer() {
    try {
        window.CBC.APP.SC.UTIL.clearDataLayer();
    } catch (error) {
        log.error(
            "(cleanDataLayer) Error trying to clean the data layer",
            error
        );
    }
}

/**
 * This function checks if the DTM window data path exists for safe deep access
 *
 * @returns {void}
 */
function checkWindow() {
    if (!windowPathExists(window, DTM_WINDOW_PATH)) {
        window.CBC = window.CBC || {};
        window.CBC.APP = window.CBC.APP || {};
        window.CBC.APP.SC = window.CBC.APP.SC || {};
        window.CBC.APP.SC.DTM = window.CBC.APP.SC.DTM || {};
    }
}

/**
 * This function determines how to handle firing off the analytics data. If analytics update fails,
 * try again on interval until threshold has been passed
 *
 * @param {object} data the analytics data
 * @returns {void}
 */
function fireAnalytics(data) {
    checkWindow();
    const limit = Date.now() + MAX_LIMIT_THRESHOLD;

    (function tryFireAnalytics() {
        try {
            fireAnalyticsData(data);
        } catch (error) {
            Date.now() < limit
                ? setTimeout(tryFireAnalytics, INTERVAL_LENGTH)
                : log.error(
                      "(fireAnalytics)",
                      new Error(TRACKING_LIMIT_REACHED)
                  );
        }
    })();
}

/**
 * This function increments the current tracking count and returns the updated value
 *
 * @returns {number} the incremented tracking count (++trackingCounter)
 */
function updateAndReturnTrackingCounter() {
    return getTrackingCounter();
}

/**
 * This function fires off the analytics data
 *
 * @param {object} data the analytics data to fire
 * @returns {void}
 */
function fireAnalyticsData(data = {}) {
    const trackingCounter = updateAndReturnTrackingCounter();

    window.CBC.APP.SC.DTM.DATA = {
        ...DEFAULT_DTM_DATA,
        ...data,
        trackingCounter,
    };
    window.CBC.APP.SC.PageTracker.init();
}

/**
 * This function is used to update the tracking counter when the load more button on a page is clicked
 *
 * @returns {void}
 */
export function fireLoadClickedAnalytics() {
    checkWindow();
    const currentData = window.CBC.APP.SC.DTM.DATA;

    fireAnalytics(currentData);
}

/**
 * This predicate function splits a period delimited path and checks that each nested level exists in an object
 *
 * @param {object} obj the object to check
 * @param {string} path the nested path to check
 *
 * @returns {boolean} true if path exists, false otherwise
 */
function windowPathExists(obj, path) {
    return path
        .split(".")
        .filter((key) => key.length)
        .reduce((acc, cur) => acc && acc[cur], obj);
}

/**
 * create featureFlag react hook for sc_data.
 */

const useFeatureFlag = createUseFeatureFlagBySection("page_analytics");

/**
 * This component fires off analytics data based on whatever data its parent feeds in
 *
 * @param {object} dtmData the data to send off to dtm
 * @param {boolean} isLanding whether or not it's a landing page
 *
 * @returns {null}
 */
export default function PageAnalytics({ dtmData = {}, type }) {
    const DATA = useFeatureFlag();

    useEffect(() => {
        if (checkAnalyticsLoaded(dtmData, type)) {
            if (locationChanged(dtmData.url)) {
                cleanDataLayer();
                fireAnalytics({ ...dtmData, ...DATA });
            }
        }
    }, [
        dtmData.url,
        dtmData.title,
        dtmData.contentarea,
        dtmData.contenttype,
        DATA,
    ]);
    return null;
}

PageAnalytics.propTypes = {
    dtmData: object,
    isLanding: bool,
};
